import React, { PropsWithChildren, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import {
  AnchorButton,
  Box,
  Button,
  Callout,
  CopyText,
  Dialog,
  Flex,
  Heading,
  Spinner,
  Tab,
  Tabs,
  Text
} from 'core/components';
import CodeMirror from 'core/components/CodeMirror';
import { getApplicationName } from '@kentik/ui-shared/query/queryUtils';
import { UdeSqlChunk, UdeSqlChunking } from '@kentik/ui-shared/query/query.types';
import $auth from 'app/stores/$auth';
import QueryModel from 'app/stores/ude/QueryModel';
import { QueryType } from 'app/stores/ude/QueryRequest';
import 'codemirror/mode/sql/sql';
import 'codemirror/addon/fold/foldcode';
import 'codemirror/addon/fold/foldgutter.css';
import 'codemirror/addon/fold/foldgutter';
import 'codemirror/addon/fold/brace-fold';
import 'codemirror/addon/fold/indent-fold';

interface Props {
  queryModel: QueryModel;
  isOpen: boolean;
  onClose: () => void;
}

const CodeMirrorWrap = styled(Box)`
  border: ${({ theme }) => theme.borders.thin};
  border-radius: 4px;
  overflow: hidden;

  .CodeMirror {
    height: 340px;
    font-size: 12px;
  }
`;

const EDITOR_OPTIONS = {
  readOnly: true,
  lineNumbers: true,
  foldGutter: true,
  gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter']
};

// CodeMirror mis-measures when it mounts while the dialog is still animating open;
// refresh once the container has settled so scrolling and the gutter render correctly.
const refreshOnMount = (editor: { refresh: () => void }) => {
  setTimeout(() => editor.refresh(), 0);
  setTimeout(() => editor.refresh(), 350);
};

function kibanaUrl(requestId: string): string | null {
  const base = $auth.kibanaUrl;
  if (!base || !requestId) {
    return null;
  }
  return `${base.replace(/\/$/, '')}/app/discover#/?_a=(query:(language:kuery,query:'request_id:%22${encodeURIComponent(
    requestId
  )}%22'))`;
}

function formatDuration(seconds: number): string {
  if (seconds <= 0) {
    return '0s';
  }
  const units: [number, string][] = [
    [86400, 'd'],
    [3600, 'h'],
    [60, 'm'],
    [1, 's']
  ];
  const parts: string[] = [];
  let remaining = Math.round(seconds);
  for (const [size, label] of units) {
    if (remaining >= size) {
      parts.push(`${Math.floor(remaining / size)}${label}`);
      remaining %= size;
    }
  }
  return parts.slice(0, 2).join(' ');
}

function formatEpoch(seconds: number): string {
  return new Date(seconds * 1000)
    .toISOString()
    .replace('T', ' ')
    .replace(/\.\d{3}Z$/, ' UTC');
}

function chunkingSummary(chunking: UdeSqlChunking | undefined): string {
  if (!chunking) {
    return '—';
  }
  if (chunking.liveUpdate) {
    return 'Not chunked (live query)';
  }
  if (!chunking.chunked) {
    return 'Not chunked (single query)';
  }
  const equalSizes = chunking.windows.every((window) => window.durationSeconds === chunking.chunkSizeSeconds);
  const size = chunking.chunkSizeSeconds
    ? ` · ${!equalSizes ? '~' : ''}${formatDuration(chunking.chunkSizeSeconds)} each`
    : '';
  return `${chunking.chunkCount} chunks${size}`;
}

const Field = ({ label, children }: PropsWithChildren<{ label: string }>) => (
  <Flex alignItems="center" gap={2}>
    <Text muted width={130} flex="0 0 auto">
      {label}
    </Text>
    {children}
  </Flex>
);

const CopyButton = ({ value }: { value: string }) =>
  value ? (
    <CopyText text={value} iconLeft iconVisible copyConfirmationText="Copied!">
      <Text small>Copy</Text>
    </CopyText>
  ) : null;

const CodeBlock = ({ value, mode }: { value: string; mode: 'sql' | 'javascript' }) => (
  <Flex flexDirection="column" gap={1}>
    <Flex justifyContent="flex-end" minHeight={20}>
      <CopyButton value={value} />
    </Flex>
    <CodeMirrorWrap>
      <CodeMirror value={value} mode={mode} options={EDITOR_OPTIONS} editorDidMount={refreshOnMount} />
    </CodeMirrorWrap>
  </Flex>
);

function UdeDebugDialog({ queryModel, isOpen, onClose }: Props) {
  const [sqlLoading, setSqlLoading] = useState(false);
  const [sqlErrorMap, setSqlError] = useState<Map<QueryType, string>>(new Map());
  const [sqlTextMap, setSqlText] = useState<Map<QueryType, string>>(new Map());
  const [chunkingMap, setChunking] = useState<Map<QueryType, UdeSqlChunking>>(new Map());
  const [chunksMap, setChunks] = useState<Map<QueryType, UdeSqlChunk[]>>(new Map());

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const { activeQueryRequests } = queryModel;

    let cancelled = false;
    setSqlLoading(true);
    setSqlError(new Map());
    setSqlText(new Map());
    setChunking(new Map());
    setChunks(new Map());

    Promise.allSettled(activeQueryRequests.map((queryRequest) => queryRequest.fetchSql()))
      .then((result) => {
        if (cancelled) {
          return;
        }

        const responses = result.filter((r) => r.status === 'fulfilled').map((r) => r.value);

        setSqlError(
          new Map(
            result.map((r, index) => {
              const { type } = activeQueryRequests[index];
              if (r.status === 'rejected') {
                return [type, 'Failed to generate SQL.'];
              }
              return [type, ''];
            })
          )
        );
        setSqlText(
          new Map(
            responses.map((response) => [
              response.type,
              response.queries.join(';\n\n') || '-- No SQL generated for this query.'
            ])
          )
        );
        setChunking(new Map(responses.map((response) => [response.type, response.chunking])));
        setChunks(new Map(responses.map((response) => [response.type, response.chunks])));
      })
      .finally(() => {
        if (!cancelled) {
          setSqlLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, queryModel]);

  const path = typeof window !== 'undefined' ? window.location.pathname : '';

  const tabs = queryModel.activeQueryRequests.map((queryRequest) => {
    const { requestId, queryDurationMs, type } = queryRequest;
    const kibana = kibanaUrl(requestId);
    const sqlError = sqlErrorMap.get(type);
    const sqlText = sqlTextMap.get(type) || '';
    const chunking = chunkingMap.get(type);
    const chunks = chunksMap.get(type) || [];

    const queryRequestData = {
      source: getApplicationName(path),
      application_metadata: queryModel.buildApplicationMetadata(),
      requestID: requestId,
      query: queryRequest.query
    };
    const rawResults = {
      results: queryRequest.results.serialize(),
      rawColumnKeys: queryRequest.rawColumnKeys,
      rawRows: queryRequest.rawRows
    };

    const sqlPanel = (
      <Flex flexDirection="column" gap={2}>
        {sqlLoading && (
          <Flex alignItems="center" gap={1} p={2}>
            <Spinner size={16} />
            <Text muted>Generating SQL…</Text>
          </Flex>
        )}
        {!sqlLoading && sqlError && (
          <Callout intent="danger" icon="error">
            {sqlError}
          </Callout>
        )}
        {!sqlLoading && !sqlError && chunking?.chunked && (
          <>
            <Callout intent="primary" icon="grid-view">
              <Flex flexDirection="column" gap="4px">
                <Text>{chunkingSummary(chunking)}</Text>
                <Text muted small>
                  Total range {formatDuration(chunking.totalDurationSeconds)} · aggregation window{' '}
                  {formatDuration(chunking.windowSizeSeconds)} · queries run in parallel per window, then results are
                  merged
                </Text>
              </Flex>
            </Callout>
            {chunks.map((chunk) => (
              <Box key={chunk.index}>
                <Flex alignItems="center" justifyContent="space-between" gap={1} mb="4px">
                  <Text small fontWeight="medium">
                    Chunk {chunk.index + 1} of {chunks.length}
                    <Text muted small ml={1}>
                      {formatEpoch(chunk.start)} → {formatEpoch(chunk.end)} ({formatDuration(chunk.durationSeconds)})
                    </Text>
                  </Text>
                  <CopyButton value={chunk.sql.join(';\n\n')} />
                </Flex>
                <CodeMirrorWrap>
                  <CodeMirror
                    value={chunk.sql.join(';\n\n')}
                    mode="sql"
                    options={EDITOR_OPTIONS}
                    editorDidMount={refreshOnMount}
                  />
                </CodeMirrorWrap>
              </Box>
            ))}
          </>
        )}
        {!sqlLoading && !sqlError && !chunking?.chunked && <CodeBlock value={sqlText} mode="sql" />}
      </Flex>
    );

    return {
      id: type,
      title: type,
      panel: (
        <>
          <Flex flexDirection="column" gap={1} mb={3}>
            <Heading level={6} mb={0}>
              Overview
            </Heading>
            <Flex flexDirection="column" gap={2}>
              <Field label="Request ID">
                {requestId ? <CopyText text={requestId} justifyContent="flex-start" /> : <Text muted>—</Text>}
              </Field>
              <Field label="Query Duration">
                <Text>{queryDurationMs != null ? `${queryDurationMs.toLocaleString()} ms` : '—'}</Text>
              </Field>
              <Field label="Chunking">
                <Text>{sqlLoading ? 'Analyzing…' : chunkingSummary(chunking)}</Text>
              </Field>
              <Field label="Kibana Logs">
                {kibana ? (
                  <AnchorButton
                    href={kibana}
                    target="_blank"
                    rel="noopener noreferrer"
                    text="View in Kibana"
                    icon="share"
                    small
                    minimal
                  />
                ) : (
                  <Text muted>Kibana URL not configured</Text>
                )}
              </Field>
            </Flex>
          </Flex>

          <Tabs id="ude-debug-info-tabs" defaultSelectedTabId="sql" renderActiveTabPanelOnly bordered>
            <Tab id="sql" title="SQL" panel={sqlPanel} />
            <Tab
              id="request"
              title="Query Request"
              panel={<CodeBlock value={JSON.stringify(queryRequestData, null, 2)} mode="javascript" />}
            />
            <Tab
              id="raw"
              title="Raw Results"
              panel={<CodeBlock value={JSON.stringify(rawResults, null, 2)} mode="javascript" />}
            />
          </Tabs>
        </>
      )
    };
  });

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="UDE Query Debug" width={840} top={40} icon="diagnosis">
      <Dialog.Body>
        {tabs.length === 1 && tabs[0].panel}
        {tabs.length > 1 && (
          <Tabs id="ude-debug-query-request-tabs" defaultSelectedTabId={tabs[0].id} bordered>
            {tabs.map((tab) => (
              <Tab key={tab.id} id={tab.id} title={tab.title} panel={tab.panel} />
            ))}
          </Tabs>
        )}
      </Dialog.Body>
      <Dialog.Footer>
        <Button text="Close" onClick={onClose} />
      </Dialog.Footer>
    </Dialog>
  );
}

export default observer(UdeDebugDialog);
